import { useEffect, useMemo } from 'react';
import { useClientApi, useClientCreateApi, useClientUpdateApi } from '@hero/api-hooks';
import { Client, getObjectId } from '@hero/schema';
import { ClientFormValues, useClientForm, UseClientFormOptions } from './useClientForm';

type ClientCreateResult = Awaited<
  ReturnType<ReturnType<typeof useClientCreateApi>['mutateAsync']>
>;

export interface UseClientCreateManagerOptions {
  /**
   * Called after create. Returns the freshly-created `Client` (the
   * primary contact lives inline on `Client.primaryContact` in the
   * relations-v2 model).
   */
  onClientSaved?: (data: ClientCreateResult) => void;
  /** Called after update (returns client only; update API does not return contact). */
  onClientUpdated?: (data: { client: Client }) => void;
  /** When set, load client for edit; when undefined, create mode. */
  clientId?: Parameters<typeof useClientApi>[0];
  /**
   * Optional initial form values (e.g. pre-fill from context).
   * Overwritten when loading an item via `clientId`.
   */
  defaultValues?: UseClientFormOptions['defaultValues'];
}

/**
 * Manager hook for the unified `Client` CRM entity (relations-v2).
 *
 * Replaces `useCustomerCreateAdapter`. The legacy single `type`
 * (`business_customer | individual_customer`) is gone — Client.roles[]
 * is multi-valued and bound via the `ClientRoleSelector` component.
 */
export const useClientCreateManager = (options: UseClientCreateManagerOptions) => {
  const { clientId, onClientSaved, onClientUpdated } = options;
  const isEdit = clientId != null;
  const clientQuery = useClientApi(clientId);
  const clientCreate = useClientCreateApi();
  const clientUpdate = useClientUpdateApi();
  const savedClient = clientQuery.data;

  const defaultValues = useMemo(
    (): Partial<ClientFormValues> => ({
      clientName: '',
      personName: '',
      personPhone: '',
      personEmail: '',
      // Start blank — the user must pick a role explicitly. The form's
      // Zod resolver enforces `roles.min(1)`, so the submit button
      // stays disabled until they do.
      roles: [],
    }),
    [],
  );

  async function onSubmit(values: ClientFormValues) {
    try {
      if (savedClient) {
        const result = await clientUpdate.mutateAsync({
          _id: getObjectId(savedClient._id),
          clientName: values.clientName,
          roles: values.roles,
          // `null` from the picker means "unlink"; `undefined` from
          // an untouched form means "no change". Both pass through.
          ...(values.linkedParty !== undefined && { linkedParty: values.linkedParty }),
        });
        onClientUpdated?.({ client: result });
      } else {
        const result = await clientCreate.mutateAsync(values);
        onClientSaved?.(result);
      }
    } catch (error) {
      console.error('Failed to save client:', error);
    }
  }

  const formHook = useClientForm({
    defaultValues: options.defaultValues ?? defaultValues,
    onSubmit,
    isEdit,
  });

  useEffect(() => {
    if (clientId != null && savedClient) {
      formHook.setClient(savedClient);
    }
  }, [clientId, savedClient, formHook.setClient]);

  const reset = () => {
    clientCreate.reset();
    clientUpdate.reset();
    if (savedClient) {
      formHook.setClient(savedClient);
    } else {
      formHook.reset(defaultValues);
    }
  };

  const onSaveClick = () => {
    formHook.form.handleSubmit(formHook.onValidSubmit)();
  };

  return {
    clientQuery,
    clientCreate,
    clientUpdate,
    formHook,
    reset,
    onSaveClick,
    savedClient,
  };
};
